import { ProductCode } from './product.model';
import { SlotCode } from './slot.model';

export const isProductCode = (value: unknown): value is ProductCode =>
  Object.values(ProductCode).includes(value as ProductCode);

export const JOBS_PAID_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBSOFFER22XS,
  ProductCode.JOBSOFFER22S,
  ProductCode.JOBSOFFER22M,
  ProductCode.JOBSOFFER22L,
  ProductCode.JOBSOFFER22XL,
  ProductCode.JOBSAPPRENTICESHIP,
] as const;

export const JOBS_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBSOFFER22FREE,
  ProductCode.JOBSSPONTANEOUSAPPLICATION,
  ProductCode.JOBSOFFSITE,
  ProductCode.JOBSGIFTEDFREE,
  ...JOBS_PAID_PRODUCT_CODES,
] as const;

export const JS24_PAID_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JS24SOLO,
  ProductCode.JS24PLUS,
  ProductCode.JS24BASIC,
  ProductCode.JS24ADVANCED,
] as const;

export const JS24_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JS24FREE,
  ...JS24_PAID_PRODUCT_CODES,
] as const;

export const JOBUP_PAID_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBUP24LITE,
  ProductCode.JOBUP24BASIC,
  ProductCode.JOBUP24ADVANCED,
  ProductCode.JOBUP24ULTIMATE,
  ProductCode.JOBUP24ULTIMATEPLUS,
  ProductCode.JOBUPAPPRENTICESHIP,
] as const;

export const JOBUP_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBUP24FREE,
  ProductCode.JOBUPSPONTANEOUSAPPLICATION,
  ProductCode.JOBUPOFFSITE,
  ProductCode.JOBUPGIFTEDFREE,
  ...JOBUP_PAID_PRODUCT_CODES,
] as const;

export const PAID_PRODUCT_CODES: ProductCode[] = [
  ...JOBS_PAID_PRODUCT_CODES,
  ...JS24_PAID_PRODUCT_CODES,
  ...JOBUP_PAID_PRODUCT_CODES,
] as const;

export const APPRENTICESHIP_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBSAPPRENTICESHIP,
  ProductCode.JOBUPAPPRENTICESHIP,
] as const;

/** The value based pricing codes, whose ladder is quoted per vacancy and priced per application. */
export const VBP_PRODUCT_CODES: ProductCode[] = [ProductCode.VBPPAID, ProductCode.VBPFREE] as const;

export const isVbpProductCode = (productCode: ProductCode): boolean =>
  VBP_PRODUCT_CODES.includes(productCode);

/**
 * Product codes that support direct redemption without the /use-credit flow.
 * These products (offline, spontaneous application, and gifted-free) are
 * redeemed directly similar to slots and PPP credits.
 */
export const DIRECT_REDEMPTION_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JOBSSPONTANEOUSAPPLICATION,
  ProductCode.JOBUPSPONTANEOUSAPPLICATION,
  ProductCode.JOBSOFFSITE,
  ProductCode.JOBUPOFFSITE,
  ProductCode.JOBSGIFTEDFREE,
  ProductCode.JOBUPGIFTEDFREE,
] as const;

/**
 * Checks if the given product code supports direct redemption.
 */
export const isDirectRedemptionProduct = (productCode: ProductCode): boolean =>
  DIRECT_REDEMPTION_PRODUCT_CODES.includes(productCode);

/**
 * Paid products that can be upgraded to a higher tier.
 * Note: Top-tier products (JOBUP24ULTIMATEPLUS, JOBSOFFER22XL, JS24ADVANCED) are NOT upgradeable.
 */
export const PAID_UPGRADEABLE_PRODUCT_CODES: ProductCode[] = [
  // JS24 paid (can upgrade within tier)
  ProductCode.JS24SOLO,
  ProductCode.JS24BASIC,
  // Jobs paid (can upgrade within tier)
  ProductCode.JOBSOFFER22XS,
  ProductCode.JOBSOFFER22S,
  ProductCode.JOBSOFFER22M,
  ProductCode.JOBSOFFER22L,
  // JobUp paid (can upgrade within tier)
  ProductCode.JOBUP24LITE,
  ProductCode.JOBUP24BASIC,
  ProductCode.JOBUP24ADVANCED,
  ProductCode.JOBUP24ULTIMATE,
] as const;

/**
 * All products that can be upgraded to a higher tier (free + paid).
 * Used by canProductBeUpgraded().
 */
export const UPGRADEABLE_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JS24FREE,
  ProductCode.JOBSOFFER22FREE,
  ProductCode.JOBUP24FREE,
  ...PAID_UPGRADEABLE_PRODUCT_CODES,
] as const;

// we show a link for jobs.ch if the product is one of the following
export const hasJobsLink = (productOrSlotCode?: ProductCode | SlotCode): boolean => {
  return Boolean(
    productOrSlotCode &&
    [
      ...JOBS_PRODUCT_CODES,
      ProductCode.JOBSSTANDARD,
      ProductCode.JS24PLUS,
      ProductCode.JS24ADVANCED,
      ProductCode.JOBUP24ADVANCED,
      ProductCode.JOBUP24ULTIMATE,
      ProductCode.JOBUP24ULTIMATEPLUS,
      SlotCode.JobsBasic,
      SlotCode.JobsAdvanced,
      SlotCode.JobsUltimate,
      SlotCode.JobsUltimatePlus,
      SlotCode.JobupAdvanced,
      SlotCode.JobupUltimate,
      SlotCode.JobupUltimatePlus,
    ].includes(productOrSlotCode)
  );
};

// we show a link for jobscout24.ch if the product is one of the following
export const hasJs24Link = (productCode?: ProductCode): boolean => {
  return Boolean(
    productCode &&
    [
      ...JS24_PRODUCT_CODES,
      ProductCode.JOBSOFFER22M,
      ProductCode.JOBSOFFER22L,
      ProductCode.JOBSOFFER22XL,
      ProductCode.JOBSSTANDARD,
      ProductCode.JOBUP24ADVANCED,
      ProductCode.JOBUP24ULTIMATE,
      ProductCode.JOBUP24ULTIMATEPLUS,
      SlotCode.JobsAdvanced,
      SlotCode.JobsUltimate,
      SlotCode.JobsUltimatePlus,
    ].includes(productCode)
  );
};

export const hasJobupLink = (productCode?: ProductCode): boolean => {
  return Boolean(
    productCode &&
    [
      ...JOBUP_PRODUCT_CODES,
      SlotCode.JobupUltimatePlus,
      SlotCode.JobupUltimate,
      SlotCode.JobupAdvanced,
      SlotCode.JobupBasic,
    ].includes(productCode)
  );
};
